import { AuthProvider } from '@tedsinc/teds-components/types/src/tedsUI/auth/providers/authProvider'
import {
  PrivilegeDto,
  PrivilegeValues,
} from '@tedsinc/teds-server/dist/src/resources/v1/core/permissions/corePermissionTypes'
import axios, { AxiosInstance } from 'axios'
import { User } from '../definitions/User'
import { HttpSettings } from '../httpSettings/HttpSettings'

export type UserInfo = Omit<User, 'privileges' | 'isAdministrator'>
export const createMockUser = (userInfo: UserInfo) => (privileges: PrivilegeDto[]) => (isAdministrator: boolean) => ({
  ...userInfo,
  privileges,
  isAdministrator,
})

export const defaultAdminInfo: UserInfo = {
  personID: '1234567890',
  fullName: 'John Doe',
  primaryPositionId: 'abc123',
  primaryPositionName: 'Head of Sales',
  primarySupervisorId: 'abc123',
  primarySupervisorFullName: 'Barbra Rothschild',
  lastLogin: '2021-03-18T04:25:53.683Z',
}

export const defaultUserInfo: UserInfo = {
  personID: '1234567890',
  fullName: 'Jane Doe',
  primaryPositionId: 'abc456',
  primaryPositionName: 'Head of Marketing',
  primarySupervisorId: 'abc456',
  primarySupervisorFullName: 'David Rockefeller',
  lastLogin: '2021-03-18T04:25:53.683Z',
}

export const defaultAdminPrivilege: PrivilegeDto[] = [
  {
    privilegeId: 'BYPASS',
    privilegeTitle: 'All Privileges',
    privilegeTypes: ['facility hierarchy', 'organizational hierarchy', 'global'],
  },
  {
    privilegeId: 'SM-MGR',
    privilegeTitle: 'Succession Manager',
    privilegeTypes: ['facility hierarchy', 'organizational hierarchy', 'global'],
  },
  {
    privilegeId: 'EXECUTIVE',
    privilegeTitle: 'Executive',
    privilegeTypes: ['facility hierarchy', 'organizational hierarchy', 'global'],
  },
  {
    privilegeId: 'IMPRT-CRSE',
    privilegeTitle: 'Import Course',
    privilegeTypes: ['facility hierarchy', 'organizational hierarchy', 'global'],
  },
]

export const defaultUserPrivilege: PrivilegeDto[] = [
  {
    privilegeId: 'PERS-VIEW',
    privilegeTitle: 'Person view',
    privilegeTypes: ['facility hierarchy', 'organizational hierarchy', 'global'],
  },
]

export const adminUser: User = createMockUser(defaultAdminInfo)(defaultAdminPrivilege)(true)
export const tedsUser: User = createMockUser(defaultUserInfo)(defaultUserPrivilege)(false)

/**
 * Returns the user and axios instance to use
 * in the application
 */
export interface UserAndRequest {
  user: User
  request: AxiosInstance
}

/**
 * To work with teds-server when testing, the user ID must be prepend the "LOGIN" namespace.
 *
 * @param personId
 * @returns
 */
export function convertUserIdToLoginId(personId: string): string {
  return `LOGIN_${personId}`
}

/**
 *
 * User full name can be null, default loader function
 *
 * @param user User
 * @returns string
 */
export const resolveFullName = (user: User): string => {
  if (typeof user.fullName === 'string') return user.fullName
  if (user.isAdministrator) {
    return 'Teds Admin'
  }
  return 'Teds User'
}

export const hasPrivilege =
  (privTarget: PrivilegeValues) =>
  (user: User): boolean =>
    user.privileges.some(priv => priv.privilegeId === privTarget)

/**
 * Creates an axios instance to use for requests
 * @param httpSettings
 * @param auth
 * @returns
 */
export function createRequestFromAuthAndSettings(
  httpSettings: HttpSettings,
  auth?: AuthProvider<unknown>
): AxiosInstance {
  const tedsServerHeaders = httpSettings.services.endpoints.tedsServer.additionalHeaders
  const headers = { ...tedsServerHeaders }
  if (auth) {
    const request = auth.request(headers)
    request.defaults.baseURL = httpSettings.services.endpoints.tedsServer.url
    return request
  } else {
    return axios.create({ headers, baseURL: httpSettings.services.endpoints.tedsServer.url })
  }
}

/**
 * Returns the the user and request to use for all requests in the application
 * @param httpSettings
 * @param auth
 * @returns
 */
export async function whoAmI(httpSettings: HttpSettings, auth?: AuthProvider<unknown>): Promise<UserAndRequest> {
  // If the operator is trying to run the application with dev mode off,
  // and auth is not supplied, throw and let the operator know
  if (!auth && !httpSettings.services.globalServiceSettings.useMockServices) {
    throw new Error(`Oops! You forgot to turn on the authentication server in http-settings.json`)
  }

  // Symbolizes for production mode.
  if (auth && !httpSettings.services.globalServiceSettings.useMockServices) {
    const request = createRequestFromAuthAndSettings(httpSettings, auth)
    const response = await request.get<User>('/api/v1/core/whoami')
    return {
      user: response.data,
      request,
    }
  }

  // when auth is not supplied and dev mode is on run with the
  // default development John Doe user.
  const { devUserID } = httpSettings.services.endpoints.tedsServer.settings
  const user: User = Object.assign({}, { personID: devUserID }, adminUser)
  const request = createRequestFromAuthAndSettings(httpSettings)
  request.defaults.headers.user_uid = convertUserIdToLoginId(devUserID)
  return { user, request }
}

export const whoAmITesting = (httpSettings: HttpSettings, user: User): UserAndRequest => {
  const request = createRequestFromAuthAndSettings(httpSettings)
  request.defaults.headers.user_uid = convertUserIdToLoginId(user.personID)
  return { user, request }
}
